/**
 * PostHog Configuration
 * User behavior analytics and product insights
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';

const INTERNAL_KEY = 'ct_internal';

/**
 * Marca como interno al navegador de quien opera el sitio. Se activa una vez con
 * `?ct_internal=1` y se apaga con `?ct_internal=0`; el valor queda en localStorage y
 * se registra como propiedad `is_internal` en cada evento, para excluirlo del LEF.
 */
export const syncInternalFlag = (
  search: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): boolean => {
  const param = new URLSearchParams(search).get(INTERNAL_KEY);
  try {
    if (param === '1') storage.setItem(INTERNAL_KEY, '1');
    else if (param === '0') storage.removeItem(INTERNAL_KEY);
    return storage.getItem(INTERNAL_KEY) === '1';
  } catch {
    return param === '1';
  }
};

export const initPostHog = (): void => {
  if (!POSTHOG_KEY) {
    console.log('PostHog key not configured, analytics disabled');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',

    // Auto-capture clicks, inputs, and page views
    autocapture: true,

    // Register environment as a super property (sent with every event)
    property_blacklist: [],  // Allow all properties

    // Capture page views on route changes
    capture_pageview: 'history_change',

    // Capture page leave events
    capture_pageleave: true,

    // Session recording (watch user sessions)
    disable_session_recording: false,
    session_recording: {
      // Mask all text inputs for privacy
      maskAllInputs: true,
      // Don't mask text content (we want to see what users are looking at)
      maskTextSelector: undefined,
    },

    // Allow first-party analytics when the browser sends Do Not Track
    respect_dnt: false,

    // Don't send data in development unless explicitly enabled
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_POSTHOG_DEBUG) {
        posthog.opt_out_capturing();
        console.log('PostHog: Development mode - capturing disabled');
      }
    },

    // Bootstrap with feature flags if needed
    bootstrap: {},

    // Persistence
    persistence: 'localStorage+cookie',
  });

  // Register environment as a super property (sent with every event)
  posthog.register({
    environment: ENVIRONMENT,
    app_version: process.env.REACT_APP_VERSION || '0.1.0',
  });

  if (syncInternalFlag(window.location.search, window.localStorage)) {
    posthog.register({ is_internal: true });
  } else {
    posthog.unregister('is_internal');
  }

  console.log(`PostHog initialized for user analytics (environment: ${ENVIRONMENT})`);
};

// Export posthog instance for manual event tracking
export { posthog };

// Helper to track custom events
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  posthog.capture(eventName, properties);
};

// Track page views (useful for SPA navigation)
export const trackPageView = (path?: string): void => {
  posthog.capture('$pageview', {
    $current_url: path || window.location.href,
  });
};

// Identify user (if you add authentication later)
export const identifyUser = (
  userId: string,
  properties?: Record<string, unknown>
): void => {
  posthog.identify(userId, properties);
};

// Reset user identity (on logout)
export const resetUser = (): void => {
  posthog.reset();
};

// Feature flags helper
export const isFeatureEnabled = (flagKey: string): boolean => {
  return posthog.isFeatureEnabled(flagKey) ?? false;
};

// Group analytics (e.g., by party, region)
export const setGroup = (
  groupType: string,
  groupKey: string,
  properties?: Record<string, unknown>
): void => {
  posthog.group(groupType, groupKey, properties);
};
